/**
 * Model prices — per-team LLM pricing (Settings → Project → Model prices).
 *
 * Lists configured models with their per-category prices and when each was last
 * used. The model name links to the detail panel, where edit and delete live.
 *
 * Styling follows UsageDashboard (Tailwind + settingsCardShell + CSS vars) —
 * the previous /configuration version hand-rolled its shell, used
 * --surface-hover as a border (invisible gridlines in dark mode) and painted
 * banners with bg-red-50 / bg-green-50, which flashed white on a dark page.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, ArrowUp, ArrowDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTeam } from 'shared/context/teamContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import { listModelPrices } from 'features/settings/api/modelPricesApi';
import ModelPriceModal from 'features/settings/components/ModelPriceModal';
import { settingsSolidButtonClass, settingsTableShellClass } from 'features/settings/components/settingsTheme';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import { sortModelRows, DEFAULT_DIR } from './modelPriceSort';
import IssuesFilter from 'features/issues/components/IssuesFilter';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
  activitySearchWrapperClass,
  activitySearchInputClass,
} from 'features/activity/styles/dataTableStyles';

const INK = 'var(--text-primary)';
const MUTED = 'var(--text-muted)';

// Display labels for known canonical keys; unknown/custom keys fall back to raw.
const PRESET_LABEL = {
  input: 'Input', output: 'Output',
  cache_read: 'Cache read', cache_write: 'Cache write',
  reasoning: 'Reasoning', audio: 'Audio', image: 'Image',
};

/**
 * `null`/absent means "no price configured"; an explicit 0 is a real, free
 * price and must not read as unset.
 */
function fmtRate(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n}`;
}

/**
 * A model the team hasn't priced is NOT unpriced — its spans are already being
 * costed from the models.dev catalog (or our fallback table), and those are the
 * rates shown in this row. The badge says where they came from, so the number
 * on screen is the number on the bill and the user knows they can override it.
 * Only a model we genuinely cannot price is called unpriced.
 */
const PRICE_SOURCE = {
  catalog: {
    label: 'Auto',
    title: 'Priced automatically from the model catalog, refreshed weekly from models.dev. '
      + 'These are the rates in use: open the model to override them or add categories.',
  },
  default: {
    label: 'Trodo default',
    title: 'Not in the model catalog, so Trodo\'s built-in rates are in use. '
      + 'Open the model to override them or add categories.',
  },
  none: {
    label: 'Unpriced',
    title: 'No rate available for this model, so its calls cost $0 in reports. '
      + 'Open the model to set prices.',
  },
};

function InheritedBadge({ source }) {
  const s = PRICE_SOURCE[source] || PRICE_SOURCE.none;
  return (
    <span
      className="text-[11px] font-normal"
      style={{ color: MUTED }}
      title={s.title}
    >
      {s.label}
    </span>
  );
}

function LastUsed({ value }) {
  if (!value) return <span style={{ color: MUTED }}>Never</span>;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return <span style={{ color: MUTED }}>—</span>;
  return <span title={d.toLocaleString()}>{formatDistanceToNow(d, { addSuffix: true })}</span>;
}

const money = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return '$0';
  return n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
};

function SortTh({ id, sort, onSort, children, className = '' }) {
  const active = sort.key === id;
  const Arrow = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      className={`${tableHeaderCellClass} ${className}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(id)}
        className={`inline-flex items-center gap-1 uppercase hover:text-[color:var(--text-primary)] ${active ? 'text-[color:var(--text-primary)]' : ''}`}
      >
        {children}
        {active ? <Arrow size={11} aria-hidden /> : null}
      </button>
    </th>
  );
}

const SOURCE_OPTIONS = [
  { value: 'custom', label: 'Custom' },
  { value: 'catalog', label: 'Auto' },
  { value: 'default', label: 'Trodo default' },
  { value: 'none', label: 'Unpriced' },
];
const SOURCE_VALUES = SOURCE_OPTIONS.map((o) => o.value);
const rowSource = (r) => (r.configured === false ? (r.price_source || 'none') : 'custom');

const ModelPricesPanel = () => {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id || selectedTeam?._id || null;
  const navigate = useNavigate();
  const { team } = useParams();
  const { can } = usePermissions();

  // Reads are APP_VIEW; writes need CONFIG_MODEL_PRICES_MANAGE. Gate the edit
  // affordances rather than letting a member fill in the form and eat a 403.
  const canManage = can(PERMISSIONS.CONFIG_MODEL_PRICES_MANAGE);

  const openModel = (model) =>
    navigate(`/${team}/settings/model-prices/${encodeURIComponent(model)}`);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sourceSel, setSourceSel] = useState(SOURCE_VALUES);
  const [providerSel, setProviderSel] = useState(null); // null = all
  const [sort, setSort] = useState({ key: 'last_used', dir: 'desc' });
  const onSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: DEFAULT_DIR[key] }
  ));

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await listModelPrices(teamId));
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load prices');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (res, payload) => {
    const warnings = res?.warnings || [];
    setAddOpen(false);
    setNotice(
      `Added ${payload.model_pattern}.` + (warnings.length ? ` ${warnings.join(' ')}` : ''),
    );
    load();
  };

  // Extra-category columns to show (union across rows).
  const extraCols = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => Object.keys(r.prices || {}).forEach((k) => {
      if (k !== 'input' && k !== 'output') set.add(k);
    }));
    return Array.from(set);
  }, [rows]);

  const providers = useMemo(
    () => Array.from(new Set(rows.map((r) => r.provider).filter(Boolean))).sort(),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortModelRows(rows, sort).filter((r) => {
      if (!sourceSel.includes(rowSource(r))) return false;
      if (providerSel && r.provider && !providerSel.includes(r.provider)) return false;
      if (!q) return true;
      return String(r.model_pattern || '').toLowerCase().includes(q)
        || String(r.provider || '').toLowerCase().includes(q);
    });
  }, [rows, query, sourceSel, providerSel, sort]);

  const filtered = query.trim() || sourceSel.length !== SOURCE_VALUES.length || providerSel;

  return (
    <div className="w-full text-left" style={{ fontFamily: "'Inter', sans-serif", color: INK }}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className={`${activitySearchWrapperClass} w-full max-w-xs`}>
          <Search size={13} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search models"
            aria-label="Search models"
            className={activitySearchInputClass}
          />
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <IssuesFilter
            sections={[
              { id: 'source', label: 'Source', options: SOURCE_OPTIONS, selected: sourceSel, onChange: setSourceSel },
              ...(providers.length > 1 ? [{
                id: 'provider',
                label: 'Provider',
                options: providers.map((p) => ({ value: p, label: p })),
                selected: providerSel || providers,
                onChange: (next) => setProviderSel(next.length === providers.length ? null : next),
              }] : []),
            ]}
          />
          {canManage && (
            <button type="button" onClick={() => setAddOpen(true)} className={settingsSolidButtonClass}>
              Add model
            </button>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mb-3 text-[12px] text-[color:var(--feedback-error)]">
          {error}{' '}
          <button type="button" onClick={load} className="font-medium underline-offset-2 hover:underline">Retry</button>
        </p>
      )}
      {notice && (
        <p role="status" className="mb-3 text-[12px] text-[color:var(--feedback-success)]">
          {notice}{' '}
          <button type="button" onClick={() => setNotice(null)} className="font-medium underline-offset-2 hover:underline" aria-label="Dismiss">
            Dismiss
          </button>
        </p>
      )}

      {loading && !rows.length ? (
        <BrandLoaderInline label="Loading model prices" />
      ) : !rows.length ? (
        <p className="py-10 text-center text-[12px]" style={{ color: MUTED }}>No models</p>
      ) : !visibleRows.length ? (
        <p className="py-10 text-center text-[12px]" style={{ color: MUTED }}>
          {filtered ? 'No models match.' : 'No models'}
        </p>
      ) : (
        <div className={settingsTableShellClass}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <caption className="sr-only">Configured model prices, per 1K tokens</caption>
              <thead>
                <tr className={tableHeaderRowClass}>
                  <SortTh id="model" sort={sort} onSort={onSort}>Model</SortTh>
                  <SortTh id="provider" sort={sort} onSort={onSort}>Provider</SortTh>
                  <th scope="col" className={tableHeaderCellClass}>Input /1K</th>
                  <th scope="col" className={tableHeaderCellClass}>Output /1K</th>
                  {extraCols.map((c) => (
                    <th key={c} scope="col" className={tableHeaderCellClass}>
                      {PRESET_LABEL[c] || c} /1K
                    </th>
                  ))}
                  <SortTh id="cost" sort={sort} onSort={onSort}>Cost 30d</SortTh>
                  <SortTh id="last_used" sort={sort} onSort={onSort}>Last used</SortTh>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  /**
                   * The whole row opens the model. It's still keyboard-operable
                   * (focusable, Enter/Space) and announced as a link, which a
                   * bare onClick on <tr> would not be.
                   */
                  <tr
                    key={row.id ?? `detected:${row.model_pattern}`}
                    role="link"
                    tabIndex={0}
                    aria-label={`View usage for ${row.model_pattern}`}
                    onClick={() => openModel(row.model_pattern)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openModel(row.model_pattern);
                      }
                    }}
                    className={`${tableRowClass} focus:outline-none focus-visible:bg-[color:var(--surface-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}
                  >
                    <td className={tableDataCellClass}>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-medium" style={{ color: INK }}>
                          {row.model_pattern}
                        </span>
                        {row.configured === false && <InheritedBadge source={row.price_source} />}
                      </span>
                    </td>
                    <td className={tableDataCellClass} style={{ color: MUTED }}>{row.provider || '—'}</td>
                    {/* Inherited rates are real and in use, but muted so a
                        glance still separates them from what the team set. */}
                    {['input', 'output', ...extraCols].map((c) => (
                      <td
                        key={c}
                        className={`${tableDataCellClass} tabular-nums`}
                        style={row.configured === false ? { color: MUTED } : undefined}
                      >
                        {fmtRate(row.prices?.[c])}
                      </td>
                    ))}
                    <td className={`${tableDataCellClass} tabular-nums`}>{money(row.cost)}</td>
                    <td className={tableDataCellClass} style={{ color: MUTED }}>
                      <LastUsed value={row.last_used} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {addOpen && (
        <ModelPriceModal
          teamId={teamId}
          initial={null}
          onClose={() => setAddOpen(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default ModelPricesPanel;
